import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, X, Copy, CheckCircle } from 'lucide-react';

interface ErrorLog {
  id: string;
  timestamp: Date;
  message: string;
  error?: any;
  userAgent: string;
  url: string;
  type: 'error' | 'warning' | 'info';
}

export function MobileErrorLogger() {
  const [errors, setErrors] = useState<ErrorLog[]>([]);
  const [showErrors, setShowErrors] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Override console methods to capture errors
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalLog = console.log;

    console.error = (...args) => {
      originalError(...args);
      addLog('error', args);
    };

    console.warn = (...args) => {
      originalWarn(...args);
      addLog('warning', args);
    };

    // Capture specific chat-related logs
    console.log = (...args) => {
      originalLog(...args);
      if (args.some(arg => 
        typeof arg === 'string' && 
        (arg.includes('Chat error') || arg.includes('sendMessage') || arg.includes('API'))
      )) {
        addLog('info', args);
      }
    };

    // Global error handler
    window.addEventListener('error', (event) => {
      addLog('error', [`Global error: ${event.message}`, event.error]);
    });

    // Unhandled promise rejection
    window.addEventListener('unhandledrejection', (event) => {
      addLog('error', [`Unhandled promise rejection: ${event.reason}`]);
    });

    // Cleanup
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      console.log = originalLog;
    };
  }, []);

  const addLog = (type: 'error' | 'warning' | 'info', args: any[]) => {
    const log: ErrorLog = {
      id: Date.now().toString(),
      timestamp: new Date(),
      message: args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' '),
      error: args.find(arg => arg instanceof Error),
      userAgent: navigator.userAgent,
      url: window.location.href,
      type
    };

    setErrors(prev => [...prev.slice(-19), log]); // Keep last 20 errors
  };

  const copyLogs = () => {
    const logText = errors.map(log => 
      `[${log.timestamp.toISOString()}] ${log.type.toUpperCase()}: ${log.message}\nUA: ${log.userAgent}\nURL: ${log.url}\n`
    ).join('\n---\n');

    navigator.clipboard.writeText(logText).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const clearLogs = () => {
    setErrors([]);
  };

  // Only show on mobile devices
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  if (!isMobile && process.env.NODE_ENV === 'production') {
    return null;
  }

  return (
    <>
      {/* Error indicator */}
      {errors.length > 0 && (
        <motion.button
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowErrors(true)}
          className="fixed bottom-4 left-4 z-50 bg-red-500 text-white p-3 rounded-full shadow-lg"
        >
          <AlertCircle className="w-6 h-6" />
          <span className="absolute -top-1 -right-1 bg-red-700 text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {errors.length}
          </span>
        </motion.button>
      )}

      {/* Error panel */}
      <AnimatePresence>
        {showErrors && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            className="fixed inset-0 z-50 bg-black/90 overflow-y-auto"
          >
            <div className="min-h-screen p-4">
              <div className="bg-gray-900 rounded-lg p-4 max-w-2xl mx-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-white text-lg font-semibold">Error Logs</h2>
                  <div className="flex gap-2">
                    <button
                      onClick={copyLogs}
                      className="text-white p-2 hover:bg-white/10 rounded"
                    >
                      {copied ? <CheckCircle className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={clearLogs}
                      className="text-white p-2 hover:bg-white/10 rounded"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => setShowErrors(false)}
                      className="text-white p-2 hover:bg-white/10 rounded"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 max-h-[70vh] overflow-y-auto">
                  {errors.map((log) => (
                    <div
                      key={log.id}
                      className={`p-3 rounded text-xs font-mono ${
                        log.type === 'error' ? 'bg-red-900/50 text-red-200' :
                        log.type === 'warning' ? 'bg-yellow-900/50 text-yellow-200' :
                        'bg-blue-900/50 text-blue-200'
                      }`}
                    >
                      <div className="text-gray-400 mb-1">
                        {log.timestamp.toLocaleTimeString()}
                      </div>
                      <pre className="whitespace-pre-wrap break-all">{log.message}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}